/**
 * Cache Manager
 *
 * Provides a centralized caching layer for Supabase queries with:
 * - TTL-based expiration (configurable per entity)
 * - Entity-specific invalidation
 * - Cache statistics tracking
 */

// Entity-specific TTL configuration (in milliseconds)
const ENTITY_TTL = {
  users: 10 * 60 * 1000,                // 10 minutes (rarely changes)
  organizations: 5 * 60 * 1000,         // 5 minutes
  polygons: 5 * 60 * 1000,              // 5 minutes
  crops: 2 * 60 * 1000,                 // 2 minutes (frequently updated)
  crop_phase_types: 10 * 60 * 1000,     // 10 minutes (reference data)
  crop_propagation_types: 10 * 60 * 1000,    // 10 minutes (reference data)
  crop_schedules: 3 * 60 * 1000,        // 3 minutes
  crop_phases: 2 * 60 * 1000,           // 2 minutes
  crop_templates: 10 * 60 * 1000,       // 10 minutes
  crop_template_propagation: 10 * 60 * 1000,  // 10 minutes (M2M reference data)
  crop_archetypes: 10 * 60 * 1000,      // 10 minutes (reference data)
  organization_users: 10 * 60 * 1000,   // 10 minutes
  tasks: 2 * 60 * 1000,                 // 2 minutes (frequently updated)
  task_types: 10 * 60 * 1000,           // 10 minutes (reference data)
  task_states: 10 * 60 * 1000,          // 10 minutes (reference data)
  seasons: 5 * 60 * 1000,              // 5 minutes
  metering: 10 * 60 * 1000,            // 10 minutes (expensive computed value)
  user_storage: 10 * 60 * 1000,        // 10 minutes
  organization_user_roles: 60 * 60 * 1000, // 60 minutes (static reference data)
  invite_tickets: 1 * 60 * 1000,       // 1 minute (freshness needed for validation)
  billing_tier_types: 60 * 60 * 1000,  // 60 minutes (reference data, changes only on migration)
  billing: 2 * 60 * 1000,              // 2 minutes (changes on each transaction)
  billing_transactions: 5 * 60 * 1000, // 5 minutes (immutable records, grow over time)
  user_billing_status: 2 * 60 * 1000,  // 2 minutes (derived from billing)
  user_features: 60 * 60 * 1000,       // 60 minutes (static reference data, changes only on migration)
  user_resolved_features: 5 * 60 * 1000, // 5 minutes (invalidated on user/billing change)
};

const DEFAULT_TTL = 5 * 60 * 1000; // 5 minutes default

class CacheManager {
  constructor() {
    this.cache = new Map();
    this.stats = {
      hits: 0,
      misses: 0,
      invalidations: 0
    };
  }

  /**
   * Generate a unique cache key from table name and query parameters
   * @param {string} table - Table name
   * @param {Object} query - Query parameters
   * @returns {string} - Cache key
   */
  generateKey(table, query) {
    return JSON.stringify({ table, ...query });
  }

  /**
   * Generate a cache key with parameter normalization
   * Ensures consistent keys for equivalent parameters (e.g., sorted arrays)
   *
   * @param {string} table - Table name
   * @param {string} method - Method name (e.g., 'getByIds')
   * @param {*} params - Parameters (single value, array, or object)
   * @returns {string} - Normalized cache key: "{table}:{method}:{params}"
   */
  generateCacheKey(table, method, params) {
    // Normalize parameters
    let normalized = params;

    if (Array.isArray(params)) {
      // Sort arrays for consistent cache keys
      normalized = [...params].sort();
    } else if (params && typeof params === 'object') {
      // Sort object keys for consistent cache keys
      const sorted = {};
      Object.keys(params).sort().forEach(key => {
        sorted[key] = Array.isArray(params[key])
          ? [...params[key]].sort()
          : params[key];
      });
      normalized = sorted;
    }

    // Format: table:method:params
    return `${table}:${method}:${JSON.stringify(normalized)}`;
  }

  /**
   * Get data from cache with TTL validation
   * @param {string} key - Cache key
   * @param {string} table - Table name (for TTL lookup)
   * @returns {*} - Cached data or null if expired/missing
   */
  get(key, table) {
    const entry = this.cache.get(key);

    if (!entry) {
      this.stats.misses++;
      return null;
    }

    // Check TTL
    const ttl = ENTITY_TTL[table] || DEFAULT_TTL;
    const age = Date.now() - entry.timestamp;

    if (age > ttl) {
      this.cache.delete(key);
      this.stats.misses++;
      return null;
    }

    this.stats.hits++;
    return entry.data;
  }

  /**
   * Store data in cache with timestamp
   * @param {string} key - Cache key
   * @param {*} data - Data to cache
   * @param {number} [customTTL] - Optional custom TTL in milliseconds
   */
  set(key, data, customTTL = null) {
    this.cache.set(key, {
      data,
      timestamp: Date.now(),
      customTTL
    });
  }

  /**
   * Check if key exists in cache (without TTL validation)
   * @param {string} key - Cache key
   * @returns {boolean}
   */
  has(key) {
    return this.cache.has(key);
  }

  /**
   * Invalidate all cache entries for a specific table/entity
   * @param {string} table - Table name to invalidate
   * @returns {number} - Number of entries invalidated
   */
  invalidate(table) {
    let count = 0;

    for (const [key] of this.cache) {
      try {
        // New format: "table:method:params"
        if (key.includes(':')) {
          const [keyTable] = key.split(':');
          if (keyTable === table) {
            this.cache.delete(key);
            count++;
          }
        } else {
          // Legacy format: JSON stringified object with table property
          const parsed = JSON.parse(key);
          if (parsed.table === table) {
            this.cache.delete(key);
            count++;
          }
        }
      } catch {
        // Skip invalid keys
        console.warn('[CacheManager] Invalid cache key format:', key);
      }
    }

    this.stats.invalidations += count;
    return count;
  }

  /**
   * Invalidate a specific cache key
   * @param {string} key - Cache key to invalidate
   * @returns {boolean} - True if key was deleted
   */
  invalidateKey(key) {
    const deleted = this.cache.delete(key);
    if (deleted) {
      this.stats.invalidations++;
    }
    return deleted;
  }

  /**
   * Clear entire cache
   */
  invalidateAll() {
    const size = this.cache.size;
    this.cache.clear();
    this.stats.invalidations += size;
  }

  /**
   * Get cache statistics
   * @returns {Object} - Stats object with hits, misses, size, hit rate
   */
  getStats() {
    const total = this.stats.hits + this.stats.misses;
    const hitRate = total > 0 ? (this.stats.hits / total * 100).toFixed(2) : 0;

    return {
      hits: this.stats.hits,
      misses: this.stats.misses,
      invalidations: this.stats.invalidations,
      size: this.cache.size,
      hitRate: `${hitRate}%`,
      hitRateNumeric: parseFloat(hitRate)
    };
  }

  /**
   * Get all cached keys (for debugging)
   * @returns {Array} - Array of { key, table, timestamp, age }
   */
  getKeys() {
    const keys = [];

    for (const [key, entry] of this.cache) {
      try {
        let table;

        // New format: "table:method:params"
        if (key.includes(':')) {
          [table] = key.split(':');
        } else {
          // Legacy format: JSON stringified object with table property
          const parsed = JSON.parse(key);
          table = parsed.table;
        }

        const age = Date.now() - entry.timestamp;
        const ttl = entry.customTTL || ENTITY_TTL[table] || DEFAULT_TTL;

        keys.push({
          key,
          table,
          timestamp: entry.timestamp,
          age,
          ttl,
          timeRemaining: Math.max(0, ttl - age),
          isExpired: age > ttl
        });
      } catch {
        // Include malformed keys
        keys.push({
          key,
          table: 'unknown',
          timestamp: entry.timestamp,
          age: Date.now() - entry.timestamp,
          malformed: true
        });
      }
    }

    return keys;
  }

  /**
   * Reset statistics (useful for testing)
   */
  resetStats() {
    this.stats = {
      hits: 0,
      misses: 0,
      invalidations: 0
    };
  }
}

// Export singleton instance
export const cacheManager = new CacheManager();

// Export class for testing
export { CacheManager };
